import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "../customAxios";
import { getDeliveryStatus, convertFileToBase64 } from "../utils/functions";

export function useDriverDeliveriesDetail(deliveryId) {
  const navigate = useNavigate();

  const [delivery, setDelivery] = useState({});
  const [finalStatuses] = useState([
    "delivered",
    "returned",
    "cancelled",
    "damaged",
  ]);
  const [imageAllowedStatus] = useState([...finalStatuses, "failed"]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [whatsapp, setWhatsapp] = useState({
    message: "",
    contact: "",
  });

  const [formState, setFormState] = useState({
    status: "",
    substatus: "",
    comment: "",
    photos: [],
  });

  const [statuses] = useState(
    getDeliveryStatus().filter((status) =>
      [
        "pending",
        "on_the_way",
        "delivered",
        "failed",
        "returned",
        "damaged",
      ].includes(status.system_name),
    ),
  );
  const [substatuses, setSubStatuses] = useState([]);

  useEffect(() => {
    const fetchDelivery = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await axios.get(
          `deliveries/get_driver_delivery/${deliveryId}/`,
        );
        setDelivery(data);
        setFormState((previous) => ({
          ...previous,
          status: data.status,
          substatus: data.substatus || "",
          comment: data.comment || "",
          photos: data.photos || [],
        }));

        setWhatsapp({
          message: `Hola, necesito ayuda con mi orden: ${data.order}`,
          contact: data.warehouse_phone,
        });
      } catch (error) {
        console.error(error);
        setError("No se pudo obtener la ruta");
      } finally {
        setLoading(false);
      }
    };
    fetchDelivery();
  }, [deliveryId]);

  const handleFormChange = useCallback(
    (field, value) => {
      setFormState((previous) => ({ ...previous, [field]: value }));

      if (field === "status") {
        const currentStatus = statuses.find((s) => s.system_name === value);
        setSubStatuses(currentStatus?.substatus || []);
        setFormState((previous) => ({ ...previous, substatus: "" }));
      }
    },
    [statuses],
  );

  const updateDelivery = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!finalStatuses.includes(delivery.status)) {
        if (!formState.status) {
          setError("Debes seleccionar un estado");
          setLoading(false);
          return;
        }

        const payload = {
          status: formState.status,
          substatus: formState.substatus,
          comment: formState.comment,
        };
        await axios.patch(`deliveries/update_delivery/${deliveryId}/`, payload);
      }

      const effectiveStatus = finalStatuses.includes(delivery.status)
        ? delivery.status
        : formState.status;
      const hasPhotos = formState.photos && formState.photos.length > 0;
      if (imageAllowedStatus.includes(effectiveStatus) && hasPhotos) {
        try {
          const base64Photos = await Promise.all(
            formState.photos.map((file) => convertFileToBase64(file)),
          );
          await axios.post(
            `deliveries/upload_delivery_evidence/${deliveryId}/`,
            { photos: base64Photos },
          );
        } catch (error) {
          setError("No se pudo actualizar el envío. Revisa tu conexión");
          console.error(`No se pudo subir la imagen: ${error}`);
        }
      }
    } catch (error) {
      setError("No se pudo actualizar la ruta");
      console.error(`No se pudo actualizar la ruta: ${error}`);
    } finally {
      setLoading(false);
      navigate("/driver/my-routes");
    }
  }, [deliveryId, delivery.status, formState, navigate]);

  const uploadDeliveryEvidence = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const base64Photos = await Promise.all(
        formState.photos.map((file) => convertFileToBase64(file)),
      );
      const payload = {
        photos: base64Photos,
      };

      await axios.post(
        `deliveries/upload_delivery_evidence/${deliveryId}/`,
        payload,
      );
    } catch (error) {
      setError("No se pudo actualizar la ruta");
      console.error(`No se pudo actualizar la ruta: ${error}`);
    } finally {
      setLoading(false);
      navigate("/driver/my-routes");
    }
  }, [deliveryId, formState, navigate]);

  return {
    delivery,
    loading,
    error,
    formState,
    statuses,
    substatuses,
    finalStatuses,
    imageAllowedStatus,
    handleFormChange,
    updateDelivery,
    uploadDeliveryEvidence,
    whatsapp,
  };
}
